import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Note } from 'sr-types/lib/search/v1/graphql';
import { failedToastMessage } from '../../../utils/commonUtils';
import type { SidePanelCommandResult, SidePanelOwnerCommandMap } from '../../types';
import { usePanelCommands } from '../../ui/hooks/usePanelCommands';
import type { NotesPanelStatus, NotesStatusNoteCallbacks } from './types';

type NotesCommand = Extract<keyof SidePanelOwnerCommandMap, `notes.${string}`>;
type CommandState = { pending: number; failed: boolean; errorToken?: string };
const initialCommandState: CommandState = { pending: 0, failed: false };

export function useNotesPanelStatusNote(status: NotesPanelStatus | undefined, isStatusNoteAction: boolean) {
    const { dispatch, close } = usePanelCommands();
    const mounted = useRef(false);
    const [commandState, setCommandState] = useState(initialCommandState);
    const [closeRequested, setCloseRequested] = useState(false);
    const [awaitingAssociation, setAwaitingAssociation] = useState(false);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const dispatchNotesCommand = useCallback(
        async <TCommand extends NotesCommand>(
            command: TCommand,
            input: SidePanelOwnerCommandMap[TCommand]['input']
        ) => {
            if (!mounted.current) return;
            if (command === 'notes.statusNote.associate') setAwaitingAssociation(true);
            setCommandState((current) => ({
                pending: current.pending + 1,
                failed: current.pending > 0 && current.failed
            }));

            let result: SidePanelCommandResult<SidePanelOwnerCommandMap[TCommand]['output']>;
            try {
                result = await dispatch<
                    SidePanelOwnerCommandMap[TCommand]['input'],
                    SidePanelOwnerCommandMap[TCommand]['output']
                >(command, input);
            } catch (error) {
                result = { status: 'failed', error };
            }

            if (!mounted.current) return;
            const failed =
                result.status !== 'ok' || (command === 'notes.statusNote.associate' && result.value === undefined);
            const errorToken = failed ? 'statusNote.toast.save.failure' : undefined;
            setCommandState((current) => ({
                pending: current.pending - 1,
                failed: current.failed || failed,
                errorToken: errorToken ?? current.errorToken
            }));
            if (failed) {
                setCloseRequested(false);
                failedToastMessage('statusNote.toast.save.failure');
            }
            if (command === 'notes.statusNote.associate') setAwaitingAssociation(false);
        },
        [dispatch]
    );

    const saveStatusNote = useCallback(
        (note: Note) => {
            dispatchNotesCommand('notes.statusNote.associate', note);
        },
        [dispatchNotesCommand]
    );
    const clearStatusNote = useCallback(() => {
        dispatchNotesCommand('notes.statusNote.clear', undefined);
    }, [dispatchNotesCommand]);
    const changeStatusNote = useCallback(
        (note: Note | null, entityId: string) => {
            if (!mounted.current) return;
            if (isStatusNoteAction) {
                setAwaitingAssociation(Boolean(note));
                if (!note) setCloseRequested(false);
            }
            dispatchNotesCommand('notes.statusNote.changed', { note, entityId });
        },
        [dispatchNotesCommand, isStatusNoteAction]
    );
    const handleClose = useCallback(() => {
        if (mounted.current) setCloseRequested(true);
    }, []);

    useEffect(() => {
        if (!closeRequested || commandState.pending > 0 || commandState.failed || awaitingAssociation) return;
        let active = true;
        const closeNotesPanel = async () => {
            let result: SidePanelCommandResult;
            try {
                result = await close();
            } catch (error) {
                result = { status: 'failed', error };
            }
            if (!active || !mounted.current) return;
            if (result.status !== 'ok' && result.status !== 'ignored') {
                setCloseRequested(false);
                setCommandState((current) => ({
                    ...current,
                    failed: true,
                    errorToken: 'statusNote.toast.save.failure'
                }));
                failedToastMessage('statusNote.toast.save.failure');
            }
        };
        closeNotesPanel();
        return () => {
            active = false;
        };
    }, [awaitingAssociation, close, closeRequested, commandState.failed, commandState.pending]);

    const callbacks = useMemo<NotesStatusNoteCallbacks | undefined>(() => {
        if (!status && !isStatusNoteAction) return undefined;
        const callbacks: NotesStatusNoteCallbacks = {
            saveFunction: saveStatusNote,
            onStatusNoteDeleted: clearStatusNote,
            onStatusNoteChanged: changeStatusNote,
            loadingState: commandState.pending > 0,
            statusNote: status?.statusNote ?? undefined,
            deletedStatusNoteId: status?.deletedStatusNoteId,
            isStatusNoteAction
        };
        return callbacks;
    }, [changeStatusNote, clearStatusNote, commandState.pending, isStatusNoteAction, saveStatusNote, status]);

    const result = { callbacks, handleClose, errorToken: commandState.errorToken };
    return result;
}
